<template>
  <article class="zone-card">
    <header class="card-header">
      <h3 class="card-title">{{ title || 'Zone Breakdown' }}</h3>
    </header>

    <div class="card-body">
      <div v-if="loading" class="zone-list duallo-scroll">
        <div v-for="n in 4" :key="n" class="zone-item">
          <div class="zone-top">
            <span class="sk sk-line" style="width: 55%"></span>
            <span class="sk sk-line" style="width: 42px; height: 1.1rem"></span>
          </div>
          <div class="zone-meta">
            <span class="sk sk-line" style="width: 72px; height: 0.7rem"></span>
            <span class="sk sk-line" style="width: 72px; height: 0.7rem"></span>
          </div>
        </div>
      </div>

      <div v-else-if="zones.length" class="zone-list duallo-scroll">
        <div v-for="zone in zones" :key="zone.name" class="zone-item">
          <div class="zone-top">
            <span class="zone-name" :title="zone.name">{{ zone.name }}</span>
            <span v-if="hasScore(zone)" class="zone-score" :class="scoreClass(zone.integrityScorePercentage)">
              {{ Math.round(zone.integrityScorePercentage) }}%
            </span>
          </div>
          <div class="zone-meta">
            <span class="zm"><v-icon size="13" color="#7aa2ff">mdi-map-marker-outline</v-icon>{{ formatNumber(zone.numberOfMarkers) }} markers</span>
            <span class="zm"><v-icon size="13" color="#f0a05a">mdi-fire</v-icon>{{ formatNumber(zone.numberOfHotspots) }} hotspots</span>
          </div>
        </div>
      </div>

      <div v-else class="zone-empty">
        <v-icon size="32" color="#4a5a60">mdi-map-marker-off-outline</v-icon>
        <span>No zone data available</span>
      </div>
    </div>
  </article>
</template>

<script>
export default {
  name: 'InspectionZoneBreakdownCard',
  props: {
    title: { type: String, default: "" },
    dataFetcher: { type: Function, required: true }
  },
  data() {
    return { zones: [], loading: true };
  },
  async mounted() {
    this.loading = true;
    try {
      const data = await this.dataFetcher();
      this.zones = (data || []).sort((a, b) => a.integrityScorePercentage - b.integrityScorePercentage);
    } catch (err) {
      console.error('Error fetching zone breakdown', err);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    formatNumber(n) {
      if (n == null) return '0';
      return Number(n).toLocaleString();
    },
    hasScore(zone) {
      return Number.isFinite(Number(zone?.integrityScorePercentage));
    },
    scoreClass(score) {
      const s = Number(score);
      if (s >= 80) return 'is-good';
      if (s >= 50) return 'is-warn';
      return 'is-bad';
    }
  }
}
</script>

<style scoped>
.zone-card {
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 14px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  color: #fff;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.card-header {
  padding: 16px 18px 12px;
}

.card-title {
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
  color: #ffffff;
  letter-spacing: 0.01em;
}

.card-body {
  flex: 1 1 auto;
  min-height: 0;
  padding: 0 14px 14px;
  display: flex;
  flex-direction: column;
}

/* Zone rows: full-width name + integrity score, with a markers/hotspots meta line. */
.zone-list {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-right: 6px;
}

.zone-item {
  flex: 0 0 auto;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.zone-top { display: flex; align-items: center; justify-content: space-between; gap: 10px; }

.zone-name {
  min-width: 0;
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.92rem;
  font-weight: 600;
  color: #fff;
}

.zone-score {
  flex: 0 0 auto;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.zone-score.is-good { background: rgba(74, 222, 128, 0.16); color: #6ee7a0; border: 1px solid rgba(74, 222, 128, 0.35); }
.zone-score.is-warn { background: rgba(245, 166, 35, 0.16); color: #f0b968; border: 1px solid rgba(245, 166, 35, 0.4); }
.zone-score.is-bad { background: rgba(248, 113, 113, 0.16); color: #ff9c9c; border: 1px solid rgba(248, 113, 113, 0.4); }

.zone-meta { display: flex; gap: 16px; font-size: 0.8rem; color: #8aa0aa; }
.zm { display: inline-flex; align-items: center; gap: 5px; font-variant-numeric: tabular-nums; }

.zone-empty {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px 16px;
  color: #8aa0aa;
  font-size: 0.9rem;
}

/* Loading shimmer */
.sk {
  position: relative;
  display: inline-block;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
}
.sk-line { height: 0.9rem; }
.sk::after {
  content: "";
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.09), transparent);
  animation: sk-shimmer 1.25s ease-in-out infinite;
}
@keyframes sk-shimmer { 100% { transform: translateX(100%); } }
@media (prefers-reduced-motion: reduce) {
  .sk::after { animation: none; }
}
</style>
